/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";

// Define types for our events and responses
interface RunEvent {
  run_id: string;
  type: string;
  command: string;
  content?: string;
  error?: string;
  status?: string;
}

export interface RunContext {
  messageId: string;
  clientId: string;
  agentId: string;
  companyId: string;
  baseUrl: string;
  repositoryId: string;
}

// Define server response types - matching FastAPI format
interface ServerMessage {
  message?: string;
  error?: string;
  client_id?: string;
  status?: string;
}

interface RunManagerOptions {
  clientId: string;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: any) => void;
}

interface RunOptions {
  agentType: "joke" | "story" | "dev" | string;
  input: string;
  runId?: string;
  context?: RunContext;
  url: string;
}

// Custom hook to manage agent runs via WebSocket
export const useAgentRunManager = ({
  clientId,
  onOpen,
  onClose,
  onError,
}: RunManagerOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [runs, setRuns] = useState<
    Record<
      string,
      {
        content: string;
        status: "running" | "completed" | "error";
        error?: string;
      }
    >
  >({});

  // Use a ref for the WebSocket instance to persist across renders
  const socketRef = useRef<WebSocket | null>(null);
  // Use a ref for message handlers to avoid recreating them on each render
  const handlersRef = useRef<Record<string, (event: RunEvent) => void>>({});

  // Connect to the WebSocket server
  const connect = useCallback(
    (url: string) => {
      // Do not try to connect if we're already connected or connecting
      if (isConnected || isConnecting) return;
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        setIsConnected(true);
        setIsConnecting(false);
        return;
      }
      if (socketRef.current?.readyState === WebSocket.CONNECTING) {
        setIsConnecting(true);
        return;
      }

      setIsConnecting(true);

      // Close any existing connection that isn't open or connecting
      if (
        socketRef.current &&
        socketRef.current.readyState !== WebSocket.OPEN &&
        socketRef.current.readyState !== WebSocket.CONNECTING
      ) {
        socketRef.current.close();
        socketRef.current = null;
      }

      // Don't create a new connection if we already have one
      if (socketRef.current) return;

      // Create a new WebSocket connection with error handling
      console.log(`Connecting to WebSocket: ${url}`);
      let socket: WebSocket;
      try {
        socket = new WebSocket(`${url}`);
      } catch (error) {
        console.error("Failed to create WebSocket connection:", error);
        setIsConnecting(false);
        onError?.(error);
        return;
      }

      socket.onopen = () => {
        console.log("WebSocket connection opened");
        setIsConnected(true);
        setIsConnecting(false);
        onOpen?.();
      };

      socket.onclose = (event) => {
        console.log(
          `WebSocket closed with code: ${event.code}, reason: ${event.reason}`,
        );
        setIsConnected(false);
        setIsConnecting(false);
        socketRef.current = null;
        onClose?.();

        // Only attempt to reconnect for normal closures or network errors
        // Don't reconnect for policy violations or server rejections
        // if (event.code === 1000 || event.code === 1001 || event.code === 1006) {
        //   console.log("Will attempt to reconnect after delay");
        //   setTimeout(() => {
        //     if (!socketRef.current && !isConnected && !isConnecting) {
        //       connect(url);
        //     }
        //   }, 5000); // Longer delay to avoid rapid reconnection attempts
        // }
      };

      socket.onerror = (err) => {
        onError?.(err);
      };

      socket.onmessage = (event) => {
        try {
          // Try to parse as JSON, but handle text messages as well
          let data: RunEvent | ServerMessage | string;

          console.log("Received message:", event);
          try {
            data = JSON.parse(event.data);
          } catch (parseError) {
            // If we can't parse as JSON, treat as plain text message
            console.log("Received non-JSON message:", event.data);

            // For all active runs, append this as content
            const runIds = Object.keys(runs);
            if (runIds.length > 0) {
              const latestRunId = runIds[runIds.length - 1];
              setRuns((prev) => ({
                ...prev,
                [latestRunId]: {
                  ...prev[latestRunId],
                  content: (prev[latestRunId]?.content || "") + event.data,
                },
              }));
            }
            return;
          }

          // If we have a string or invalid format, skip processing
          if (typeof data !== "object" || data === null) {
            console.log("Received non-object message:", data);
            return;
          }

          // {"run_id":"run-1744300472415-c4lu1dg","type":"status","status":"running","message":"Creating code plan..."}

          // {"run_id":"run-1744300472415-c4lu1dg","type":"status","status":"code_context_completed","message":"Code context analysis completed"}

          // Handle different message types
          if (typeof data === "object" && "type" in data && "status" in data) {
            // This is the initial connection confirmation from FastAPI
            console.log("Server confirmed connection:", data);
            setIsConnected(true);
            setIsConnecting(false);
            // No need to update any runs for this message
          } else if ("run_id" in data) {
            // This is a standard RunEvent
            // Update run state based on event type
            if (data.type === "content") {
              setRuns((prev) => ({
                ...prev,
                [data.run_id]: {
                  ...prev[data.run_id],
                  content:
                    (prev[data.run_id]?.content || "") + (data.content || ""),
                },
              }));
            } else if (data.type === "completed") {
              setRuns((prev) => ({
                ...prev,
                [data.run_id]: {
                  ...prev[data.run_id],
                  status: "completed",
                },
              }));
            } else if (data.type === "error") {
              setRuns((prev) => ({
                ...prev,
                [data.run_id]: {
                  ...prev[data.run_id],
                  status: "error",
                  error: data.error,
                },
              }));
            }

            // Call any registered handler for this run
            if (handlersRef.current[data.run_id]) {
              handlersRef.current[data.run_id](data as RunEvent);
            }
          } else if ("message" in data || "error" in data) {
            // This is a FastAPI-style message
            console.log("Received server message:", data);

            // Find the most recent active run, if any
            const runIds = Object.keys(runs).filter(
              (id) => runs[id].status === "running",
            );

            if (runIds.length > 0) {
              const latestRunId = runIds[runIds.length - 1];

              if ("message" in data && data.message) {
                // Add server message to the latest run
                setRuns((prev) => ({
                  ...prev,
                  [latestRunId]: {
                    ...prev[latestRunId],
                    content: (prev[latestRunId]?.content || "") + data.message,
                  },
                }));
              } else if ("error" in data && data.error) {
                // Set error on the latest run
                setRuns((prev) => ({
                  ...prev,
                  [latestRunId]: {
                    ...prev[latestRunId],
                    status: "error",
                    error: data.error,
                  },
                }));
              }
            }
          } else {
            // Handle other message types
            console.log("Received unrecognized message format:", data);
          }
        } catch (err) {
          console.error("Error processing WebSocket message:", err);
        }
      };

      socketRef.current = socket;
    },
    [clientId, onOpen, onClose, onError],
  );

  // Start a new run
  const startRun = useCallback(
    ({ agentType, input, runId, context, url }: RunOptions) => {
      // Generate a run ID if not provided
      const actualRunId =
        runId ||
        `run-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Initialize the run in our state (even before connection)
      setRuns((prev) => ({
        ...prev,
        [actualRunId]: {
          content: "",
          status: "running",
          url: url,
        },
      }));

      // If not connected, ensure we connect first
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        connect(url);

        // Create a connection promise to track when connection is established
        const connectionPromise = new Promise<void>((resolve) => {
          // Use a timeout to prevent indefinite waiting
          const timeout = setTimeout(() => {
            clearInterval(checkConnection);
            setRuns((prev) => ({
              ...prev,
              [actualRunId]: {
                ...prev[actualRunId],
                status: "error",
                error: "Connection timeout",
              },
            }));
          }, 10000); // 10 second timeout

          // Check connection status periodically
          const checkConnection = setInterval(() => {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              clearInterval(checkConnection);
              clearTimeout(timeout);
              resolve();
            }
          }, 100);
        });

        // When connection is established, send the run
        connectionPromise
          .then(() => {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              // Send the start command - using format compatible with FastAPI
              socketRef.current.send(
                JSON.stringify({
                  command: "start_run",
                  type: "start_run",
                  agent_type: agentType,
                  message: input,
                  run_id: actualRunId,
                  context: context,
                }),
              );
            }
          })
          .catch((error) => {
            console.error("Failed to establish connection:", error);
            setRuns((prev) => ({
              ...prev,
              [actualRunId]: {
                ...prev[actualRunId],
                status: "error",
                error: "Failed to establish connection",
              },
            }));
          });
      } else {
        // We're already connected, send immediately - using format compatible with FastAPI
        socketRef.current.send(
          JSON.stringify({
            command: "start_run",
            type: "start_run",
            agent_type: agentType,
            message: input,
            run_id: actualRunId,
            context: context,
          }),
        );
      }

      return actualRunId;
    },
    [connect],
  );

  // Register a custom handler for a specific run
  const onRunEvent = useCallback(
    (runId: string, handler: (event: RunEvent) => void) => {
      handlersRef.current[runId] = handler;

      // Return a function to remove the handler
      return () => {
        delete handlersRef.current[runId];
      };
    },
    [],
  );

  // Disconnect function - manual control
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log("Manually disconnecting WebSocket");
      socketRef.current.close();
      socketRef.current = null;
      setIsConnected(false);
      setIsConnecting(false);
    }
  }, []);

  // Only clean up on unmount - no auto-connect
  useEffect(() => {
    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        console.log("Closing WebSocket connection on component unmount");
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, []);

  return {
    isConnected,
    isConnecting,
    runs,
    startRun,
    onRunEvent,
    connect,
    disconnect,
  };
};
